import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { isEmptyArray, isEmptyObject, isStringEmpty, parseEmptyArray, stringEmptyDefault } from '@common/helpers';
import { SeatReservationStatus, SegmentMap } from '@common/interfaces/servicing-seatmap/seat-reservation-status';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { SupportComponent } from '@lib/components/support-class';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import {
  GetOrderResponseData,
  OrderSeatItem,
  OrderSeatItemSeatSelection,
  Traveler,
  Type1,
  UpdateServicesRequestSeatsChildSeatsInner,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-seat-reservation-item',
  templateUrl: './seat-reservation-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeatReservationItemComponent extends SupportComponent {
  @Input() flight!: Type1;
  @Input() traveler!: Traveler;
  @Input() pnr!: GetOrderResponseData;
  @Input() seatReservationStatus!: SeatReservationStatus;

  constructor(
    private _common: CommonLibService,
    public change: ChangeDetectorRef,
    private _myBookingServiceService: MyBookingServiceService,
    private _staticMsgPipe: StaticMsgPipe
  ) {
    super(_common);
  }

  init(): void {}

  /** [3.12] 座席番号 */
  getSeatNumber(flightId?: string, travelerId?: string): Array<string> {
    let seatNumbers: Array<string> = [];
    const seatItemPnr: OrderSeatItem = this._myBookingServiceService.getSeatItemPnr(this.pnr, flightId, travelerId);
    // 申込操作中セグメント情報
    const hasSegmentMap = !isEmptyObject(this.seatReservationStatus?.[String(flightId)] as SegmentMap);
    const seatReservation = this._myBookingServiceService.getSeatItemStore(flightId, travelerId);
    const checkSeatServiceStatus = (seatSelection?: OrderSeatItemSeatSelection) => {
      return Boolean(
        seatSelection?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.Prebooked ||
          seatSelection?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.Confirmed ||
          seatSelection?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.Requesting
      );
    };
    // 1.1.申込操作中搭乗者情報.カウチ席番.座席番号が存在する場合、申込操作中搭乗者情報.カウチ席番号.座席番号を表示する。
    if (
      isEmptyArray(seatReservation?.couchSeatNumber) !== true &&
      !isStringEmpty(seatReservation?.couchSeatNumber?.[0]?.seatNumber)
    ) {
      seatNumbers = seatReservation?.couchSeatNumber?.map((seatNumber) => seatNumber.seatNumber) ?? [];
    } else if (
      isStringEmpty(seatItemPnr?.seatSelection?.couchSeatNumber) !== true &&
      (hasSegmentMap || checkSeatServiceStatus(seatItemPnr?.seatSelection))
    ) {
      /**
       *  1.2.上記以外 かつ 申込済み搭乗者情報.seatSelection.couchSeatNumberが存在する かつ
       * (申込操作中セグメント情報が存在する
       * または申込済み搭乗者情報.seatSelection.serviceStatus="prebooked"、または"confirmed"　または、"requesting"のいずれかに該当する（(カウチ席の場合は、当該カウチに座る全搭乗者))場合、申込済み搭乗者情報.seatSelection.couchSeatNumberを表示する。
       */
      seatNumbers.push(seatItemPnr?.seatSelection?.couchSeatNumber as string);
    } else if (isEmptyArray(seatReservation?.seatNumber) !== true) {
      // 2.1.上記以外　かつ、　申込操作中搭乗者情報.座席番号リスト.座席番号が存在する場合、申込操作中搭乗者情報.座席番号を表示する。
      seatNumbers = parseEmptyArray(
        seatReservation?.seatNumber?.map((seatNumber) =>
          // ※「申込操作中搭乗者情報.座席番号リスト.座席番号=””」の場合、「未登録の旨」を表示する。
          isStringEmpty(seatNumber.seatNumber) ? this._staticMsgPipe.transform('label.hyphen') : seatNumber.seatNumber
        )
      );
    } else if (
      isEmptyArray(seatItemPnr?.seatSelection?.seatNumbers) !== true &&
      (hasSegmentMap || checkSeatServiceStatus(seatItemPnr?.seatSelection))
    ) {
      /**
       * 2.2.上記以外　かつ、　申込済み搭乗者情報.seatSelection.seatNumbersが存在する　かつ、
       * (申込操作中セグメント情報が存在する　または申込済み搭乗者情報.seatSelection.serviceStatus="prebooked"　または、"confirmed"、または"requesting"のいずれかに該当する
       * (カウチ席の場合は、当該カウチに座る全搭乗者))場合、申込済み搭乗者情報.seatSelection.seatNumbersを表示する。 ※複数件の場合、区切り文字で連結して繰り返し設定
       */
      seatNumbers = [...(seatItemPnr?.seatSelection?.seatNumbers ?? [])];
    } else if (
      isEmptyArray(seatItemPnr?.seatSelection?.seatNumbers) !== true &&
      seatItemPnr?.seatSelection?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.Cancelled
    ) {
      // 2.3.申込済み搭乗者情報.seatSelection.seatNumbersが存在する、かつ申込済み搭乗者情報.seatSelection.serviceStatus = ""cancelled""の場合、表示しない(※座席ステータスのみが表示される)。
      seatNumbers = [];
    } else if (isEmptyArray(seatItemPnr?.seatSelection?.seatNumbers)) {
      // 2.4.申込済み搭乗者情報.seatSelection.seatNumbersが存在しない場合、未登録の旨"
      seatNumbers.push(this._staticMsgPipe.transform('label.hyphen'));
    }
    return seatNumbers;
  }

  /** [3.13] 座席属性 */
  getSeatAttributes(flightId?: string, travelerId?: string): string {
    const seatItemPnr: OrderSeatItem = this._myBookingServiceService.getSeatItemPnr(this.pnr, flightId, travelerId);
    const seatItemStore = this._myBookingServiceService.getSeatItemStore(flightId, travelerId);
    // 1申込操作中搭乗者情報.座席属性が存在する場合、申込操作中情報.座席属性を引数として、「当画面共通処理No5.座席属性名称取得処理」を実行し、返却値の座席属性名称を表示する。
    // 2.上記以外 かつ 申込済み搭乗者情報.seatSelection.characteristicRequestSsrCodeが存在する場合、申込済み搭乗者情報.seatSelection.characteristicRequestSsrCodeを引数として、「当画面共通処理No5.座席属性名称取得処理」を実行し、返却値の座席属性名称を表示する。"
    if (isStringEmpty(seatItemStore?.seatAttribute) !== true) {
      return this._myBookingServiceService.onHandleCommonNo5(seatItemStore?.seatAttribute);
    } else if (isStringEmpty(seatItemPnr?.seatSelection?.characteristicRequestSsrCode) !== true) {
      return this._myBookingServiceService.onHandleCommonNo5(seatItemPnr?.seatSelection?.characteristicRequestSsrCode);
    }
    return '';
  }

  /** [3.14] チャイルドシート */
  isChildSeat(flight?: Type1, traveler?: Traveler): boolean {
    const seatItemPnr: OrderSeatItem = this._myBookingServiceService.getSeatItemPnr(this.pnr, flight?.id, traveler?.id);
    const seatChildStore = this._myBookingServiceService.getChildSeatStore(traveler?.id);

    // 1.1 当該travelers.id[] IN 申込操作中搭乗者情報.搭乗者ID かつ申込操作中搭乗者情報.チャイルドシート.チャイルドシート更新区分="request"(登録)
    if (seatChildStore?.updateCategory === UpdateServicesRequestSeatsChildSeatsInner.OperationEnum.Request) {
      return true;
    }

    // 1.2.申込済み搭乗者情報.childSeat=true　かつ、　申込済み搭乗者情報.seatSelection.serviceStatus ≠ "cancelled"(削除) かつ申込操作中搭乗者情報.チャイルドシート.チャイルドシート更新区分="request"(登録)

    if (
      seatItemPnr?.childSeat === true &&
      seatItemPnr.seatSelection?.serviceStatus !== OrderSeatItemSeatSelection.ServiceStatusEnum.Cancelled
    ) {
      return seatChildStore
        ? seatChildStore?.updateCategory === UpdateServicesRequestSeatsChildSeatsInner.OperationEnum.Request
        : true;
    }

    return false;
  }

  /** [3.15] バシネット */
  isBassinet(flight?: Type1, traveler?: Traveler): boolean {
    const seatItemPnr: OrderSeatItem = this._myBookingServiceService.getSeatItemPnr(this.pnr, flight?.id, traveler?.id);
    const seatItemStore = this._myBookingServiceService.getSeatItemStore(flight?.id, traveler?.id);
    const SEAT_CODE_BASSINET = ['B', 'I'];
    // [3.15.1.2] 上記以外の場合、申込済み搭乗者情報.seatSelection.seatCharacteristics.codeに"B”または”I”(バシネットコード)が含まれる
    const hasSeatBassinetInPnr = seatItemPnr?.seatSelection?.seatCharacteristics?.some((seat) =>
      SEAT_CODE_BASSINET.includes(stringEmptyDefault(seat?.code))
    );
    // [3.15.1.3] 申込操作中搭乗者情報が存在する場合、申込操作中搭乗者情報.seatSelection.seatCharacteristics[].codeに"B"または”I”(バシネットコード)が含まれる ※申込中の座席がバシネット
    const hasSeatBassinetInStore =
      isEmptyObject(seatItemStore) !== true &&
      seatItemStore?.seatCharacteristics?.some((seat) => SEAT_CODE_BASSINET.includes(stringEmptyDefault(seat?.code)));
    // [3.15.1] 1.申込済み搭乗者情報.bassinet=trueかつ、以下条件のいずれかを満たす場合、バシネット利用のリクエストがされている旨を表示する。
    return Boolean(seatItemPnr?.bassinet && (hasSeatBassinetInPnr || hasSeatBassinetInStore));
  }

  /** [3.16] 座席ステータス */
  getSeatServiceStatus(flight?: Type1, traveler?: Traveler): string {
    let serviceStatusText = '';
    const seatItemPnr: OrderSeatItem = this._myBookingServiceService.getSeatItemPnr(this.pnr, flight?.id, traveler?.id);
    const seatItemStore = this._myBookingServiceService.getSeatItemStore(flight?.id, traveler?.id);
    const seatChildStore = this._myBookingServiceService.getChildSeatStore(traveler?.id);
    /**
     * 1.以下条件のいずれかを満たす場合、申込操作中の旨
     * 1.1.申込操作中搭乗者情報.座席番号リスト.座席番号 が存在する。
     * ※申込操作中搭乗者情報.座席番号リスト.座席番号=””の場合が含まれる
     * 1.3.申込操作中情報.座席属性 != ""
     * 1.4.当該travelers.id[] IN 申込操作中搭乗者情報.搭乗者ID かつ 申込操作中搭乗者情報.チャイルドシート.チャイルドシート更新区分 != ""(新規申込、削除、チャイルドシート種類選択の変更のいずれか)
     */
    if (
      (seatItemStore && !isEmptyArray(seatItemStore?.seatNumber)) ||
      (seatItemStore && seatItemStore?.seatAttribute !== '') ||
      (seatChildStore && seatChildStore?.updateCategory !== '')
    ) {
      return 'rabel.unpaid';
    } else {
      /** 2.上記以外かつ、申込済み搭乗者情報.seatSelection.serviceStatusが以下の場合、※値に応じてラベル文言を表示： */
      switch (seatItemPnr?.seatSelection?.serviceStatus) {
        /** 2.1.“prebooked”の場合、システム処理中の旨 */
        case OrderSeatItemSeatSelection.ServiceStatusEnum.Prebooked:
          serviceStatusText = 'label.appliedInPart';
          break;

        /** 2.2.“cancelled”の場合、キャンセルの旨 */
        case OrderSeatItemSeatSelection.ServiceStatusEnum.Cancelled:
          serviceStatusText = 'label.notApplied';
          break;

        /** 2.3.“requesting”の場合、リクエスト中の旨 */
        case OrderSeatItemSeatSelection.ServiceStatusEnum.Requesting:
          serviceStatusText = 'label.applying';
          break;
        /** 2.4."confirmed"の場合、表示しない*/
        case OrderSeatItemSeatSelection.ServiceStatusEnum.Confirmed:
          serviceStatusText = '';
          break;
      }
    }
    return serviceStatusText;
  }

  /** [3.17] 有料サービス金額 */
  isShowPaidServiceAmount(flightId?: string, traveleId?: string): boolean {
    // 申込操作中搭乗者情報.有料サービス金額 ≠ """"(空欄)、申込操作中情報.有料サービス金額を表示する。
    const seatItemStore = this._myBookingServiceService.getSeatItemStore(flightId, traveleId);
    return isStringEmpty(seatItemStore?.paidServiceAmount) !== true;
  }

  /** [3.17] 有料サービス金額 */
  getPaidServiceAmount(flightId?: string, traveleId?: string): number {
    const seatItemStore = this._myBookingServiceService.getSeatItemStore(flightId, traveleId);
    return Number(seatItemStore?.paidServiceAmount ?? 0);
  }

  override destroy(): void {}
  override reload(): void {}
}
